import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  IAPISParty,
  EmploymentCategoryDescriptionCode,
  AttributeDescriptionCodeGender,
  IDocument,
  PartyFunctionCode,
  PartyDocumentNameCode,
  ISimpleUser,
  IPartialCountryCode,
  IPartialAPISParty,
} from "@vmosolutions/oc-interfaces"; //IAircraftType
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { Button, ButtonGroup, Grid, Typography, Modal, Paper } from "@mui/material";
import * as Yup from "yup";
import { APISDocumentField, getEnumValueFromKey } from "./APISDocumentField";
import {
  FormikDateTimeField,
  FormikSelectField,
  FormikTextField,
  FormikSwitchField,
} from "../../../MUI5FormikComponents/FormikWrappers";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";

interface IAPISFormProps {
  selectedParty: IAPISParty;
  APISParties: IPartialAPISParty[];
  updateAPISList?: (party: IAPISParty, mode: "create" | "update" | "delete") => void;
  setActiveModal: (activeModal: IActiveModal | null) => void;
}

const initialFieldValues: IAPISParty = {
  partyType: PartyFunctionCode.CrewMember,
  user_id: undefined,
  firstName: "",
  middleName: undefined,
  lastName: "",
  streetAddress: undefined,
  city: undefined,
  provOrState: undefined,
  postal: undefined,
  country: undefined,
  gender: AttributeDescriptionCodeGender.Male, // 'M',
  dateOfBirth: new Date(),
  birthCountry: undefined,
  birthCity: undefined,
  birthState: undefined,
  countryOfResidence: undefined,
  nationality: "",
  APISEmploymentCategory: EmploymentCategoryDescriptionCode.CargoNonCockpitCrew,
  documents: [],
};

const employmentCategoryOptions = Object.keys(EmploymentCategoryDescriptionCode)
  .map((key) => ({
    label: key, //Passport
    value: EmploymentCategoryDescriptionCode[key as keyof typeof EmploymentCategoryDescriptionCode],
  }))
  .sort((a, b) => {
    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
  });

const genderOptions = Object.keys(AttributeDescriptionCodeGender).map((key) => ({
  label: key, //Male
  value: AttributeDescriptionCodeGender[key as keyof typeof AttributeDescriptionCodeGender], //M
}));

const partyTypeOptions = Object.keys(PartyFunctionCode)
  .map((key) => ({
    label: key, //Male
    value: PartyFunctionCode[key as keyof typeof PartyFunctionCode], //M
  }))
  .sort((a, b) => {
    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
  });

// Configuration revision validation with YUP
export const APISFieldsSchema = Yup.object().shape({
  //Required Fields
  partyType: Yup.mixed().oneOf(Object.values(PartyFunctionCode)).required(),
  firstName: Yup.string()
    .matches(
      /^[a-zA-Z* '.-\s]{0,35}$/,
      "Alphabetic, no numeric or special characters, except dash ( - ) and single quote ( ' ) and max length 35 characters"
    ) //on the backend I replace the ".-" with a space
    .required("First Name is required"),
  lastName: Yup.string()
    .matches(
      /^[a-zA-Z* '.-\s]{0,35}$/,
      "Alphabetic, no numeric or special characters, except dash ( - ) and single quote ( ' ) and max length 35 characters"
    ) //on the backend I replace the ".-" with a space
    .required("Last Name is required"),
  streetAddress: Yup.string().matches(
    /^[a-zA-Z0-9* #'.-\s]{0,35}$/,
    "Must be alpha numeric or #'.- and max length 35 characters"
  ),
  city: Yup.string().matches(/^[a-zA-Z0-9* #'.-\s]{0,35}$/, "Must be alpha numeric and max length 35 characters"),
  provOrState: Yup.string().length(2),
  postal: Yup.string().matches(/^[a-zA-Z0-9\s]{1,17}$/, "Must be alpha numeric and max length 17 characters"),
  country: Yup.string().length(3).nullable(),
  gender: Yup.mixed().oneOf(Object.values(AttributeDescriptionCodeGender)).required("Gender is required"),
  dateOfBirth: Yup.string().required("Date of Birth is required"),
  birthCountry: Yup.string().required("Birth Country is required").length(3),
  birthCity: Yup.string()
    .matches(/^[a-zA-Z0-9* #'.-\s]{0,70}$/, "Must be alpha numeric or #'.- and max length 70 characters")
    .required("Birth City is required")
    .nullable(),
  countryOfResidence: Yup.string().length(3).nullable(),
  nationality: Yup.string().required("Nationality is required").length(3),
  APISEmploymentCategory: Yup.mixed()
    .oneOf(Object.values(EmploymentCategoryDescriptionCode))
    .required("Employment Type is required"),
  // Optional fields.
  user_id: Yup.string().nullable(),
  middleName: Yup.string()
    .matches(
      /^[a-zA-Z* '.-\s]{0,35}$/,
      "Alphabetic, no numeric or special characters, except dash ( - ) and single quote ( ' ) and max length 35 characters"
    ) //on the backend I replace the ".-" with a space
    .nullable(),
  birthState: Yup.string()
    .matches(/^[a-zA-Z0-9* #'.-\s]{0,70}$/, "Must be alpha numeric or #'.- and max length 70 characters")
    .nullable(),
  documents: Yup.array(),
});

type apisForm = FormikProps<IAPISParty>;

/**
 * Form to edit selected aircraft configuration revision
 * @param props - selected aircraft
 */
export const APISForm = (props: IAPISFormProps): JSX.Element => {
  const dispatch = useDispatch();
  const { selectedParty, setActiveModal, updateAPISList, APISParties } = props;
  const [initialValues, setInitialValues] = useState<IAPISParty>(selectedParty ?? initialFieldValues);
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [countryCodeOptions, setCountryCodeOptions] = useState<IPartialCountryCode[]>([]);
  const [partyIsUser, setPartyIsUser] = useState<boolean>(false);
  const [openDocumentModal, setOpenDocumentModal] = useState<boolean>(false);
  const [users, setUsers] = useState<ISimpleUser[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<IDocument>();

  useEffect(() => {
    setInitialValues(selectedParty ?? initialFieldValues);
  }, [selectedParty]);

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<ISimpleUser>("/users?simpleUser=true");
      if (Array.isArray(response)) setUsers(response);
    };
    getUsersAction();
  }, []);

  useEffect(() => {
    const getCountryCodesAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IPartialCountryCode[]>("/structures/countryCodes");
      if (Array.isArray(response)) {
        setCountryCodeOptions(
          response.sort((a, b) => {
            return a.countryName.toLowerCase().localeCompare(b.countryName.toLowerCase());
          })
        );
      }
    };
    if (!countryCodeOptions.length) getCountryCodesAction();
  }, [countryCodeOptions.length]);

  const allUsersOptions = useMemo(() => {
    if (users) {
      return users
        .map((user) => ({
          label: `${user.lastName}, ${user.firstName} - ${user.email}`,
          value: user._id,
        }))
        .sort((a, b) => {
          return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
        });
    } else {
      return [];
    }
  }, [users]);

  const filteredUsersOptions = useMemo(() => {
    if (allUsersOptions) {
      return !APISParties
        ? allUsersOptions
        : allUsersOptions
            .filter((option) => {
              const isAlreadyAssociated = APISParties.find(
                (party: IPartialAPISParty) => party.user_id === option.value
              );
              return isAlreadyAssociated && option.value !== selectedParty?.user_id ? null : option;
            })
            .sort((a, b) => {
              return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
            });
    } else {
      return [];
    }
  }, [APISParties, allUsersOptions, selectedParty]);

  useEffect(() => {
    if (selectedParty) {
      setDocuments(
        selectedParty.documents.sort((a, b) => {
          return a.documentType.toLowerCase().localeCompare(b.documentType.toLowerCase());
        })
      );
      setPartyIsUser(selectedParty.user_id ? true : false);
    } else {
      setDocuments([]);
      setPartyIsUser(false);
    }
  }, [selectedParty]);

  useEffect(() => {
    setSelectedDocument(selectedParty?.documents?.[0]);
  }, [selectedParty]);

  const updateOnlyMCLMessage = useCallback(
    (newValues: IAPISParty): boolean => {
      if (
        initialValues.lastName !== newValues.lastName ||
        initialValues.firstName !== newValues.firstName ||
        initialValues.middleName !== newValues.middleName ||
        initialValues.gender !== newValues.gender ||
        initialValues.dateOfBirth !== newValues.dateOfBirth
      ) {
        return false;
      } else {
        return true;
      }
    },
    [initialValues]
  );

  const handleSubmit = async (values: IAPISParty, helpers: FormikHelpers<IAPISParty>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    const mode = values._id ? "update" : "create";
    const isUpdateOnly: boolean = updateOnlyMCLMessage(values);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} APIS Party...`,
      successMessage: `APIS Party Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} APIS Party`,
    };
    const response =
      mode === "create"
        ? await AmplifyAPIWrapper.Post<IAPISParty>("/APIS", values, notifications)
        : isUpdateOnly
          ? await AmplifyAPIWrapper.Put<IAPISParty>(`/APIS/updateOnly/${values._id}`, values, notifications)
          : await AmplifyAPIWrapper.Put<IAPISParty>(`/APIS/${values._id}`, values, notifications);
    if (response?._id) {
      resetForm({ values });
      setInitialValues(values);
      setStatus({ success: true });
      if (updateAPISList) updateAPISList(response, mode);
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  const selectedIndex = documents.findIndex((doc) => doc.documentNumber === selectedDocument?.documentNumber);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={APISFieldsSchema}
    >
      {(apisFieldsForm: apisForm) => {
        const { dirty, isValid, isSubmitting, handleReset } = apisFieldsForm;
        const handleDelete = (): void => {
          if (!selectedParty) {
            return;
          }
          setActiveModal({
            title: "Delete Party",
            message:
              "Are you certain you want to delete this party's APIS details? This operation cannot be undone. If this party is listed as a rider on any upcoming flights, they will be removed from the flight.",
            cancel: () => setActiveModal(null),

            confirm: async (): Promise<void> => {
              setActiveModal(null);
              const notifications: INotifications = {
                dispatch: dispatch,
                infoMessage: "Deleting APIS Party...",
                successMessage: "Party Deleted",
                errorMessage: "Error: Unable To Delete Party",
              };
              const response: any = await AmplifyAPIWrapper.Del<IAPISParty>(
                `/APIS/${selectedParty._id}`,
                undefined,
                notifications
              );
              if (response?._id) {
                apisFieldsForm.resetForm({ values: initialFieldValues });
                setInitialValues(initialFieldValues);
                apisFieldsForm.setStatus({ success: true });
                if (updateAPISList) updateAPISList(selectedParty, "delete");
              } else {
                apisFieldsForm.setStatus({ success: false });
              }
              apisFieldsForm.setSubmitting(false);
            },
            confirmLabel: "Delete",
          });
        };

        const handleUpdateDocuments = (doc: IDocument, mode: "create" | "update" | "delete"): void => {
          const newDocValues = [...documents];
          switch (true) {
            case mode === "create":
              newDocValues.push(doc);
              break;
            case mode === "update" && selectedIndex > -1:
              newDocValues[selectedIndex] = doc;
              break;
            case mode === "delete" && selectedIndex > -1:
              newDocValues.splice(selectedIndex, 1);
              break;
            default:
          }

          setDocuments(newDocValues);
          apisFieldsForm.setFieldValue("documents", newDocValues);
          apisFieldsForm.setFieldTouched("documents", true);
        };

        const handleIsAUserChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
          setPartyIsUser((prevPartyIsUser) => !prevPartyIsUser);
          if (partyIsUser) {
            apisFieldsForm.setFieldValue("user_id", null);
            apisFieldsForm.setFieldTouched("user_id", true);
            apisFieldsForm.setFieldValue("isUser", !e.target.value);
          }
        };

        return (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
                  Required Fields
                </Typography>
                <FormikSwitchField
                  label="Is A User"
                  defaultValue={selectedParty?.user_id ? true : false}
                  name="isUser"
                  onChange={handleIsAUserChange}
                />
                {partyIsUser && (
                  <FormikSelectField
                    name="user_id"
                    label="User"
                    options={filteredUsersOptions}
                    parse={(option: any) => option.value}
                    format={(userId: string) =>
                      userId ? allUsersOptions.find((user: any) => userId === user?.value) : null
                    }
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isSearchable={true}
                    isClearable={false}
                    menuPosition="fixed"
                  />
                )}
                <FormikSelectField
                  name="partyType"
                  label="Party Type"
                  options={partyTypeOptions.sort((a, b) => {
                    return a.value.toLowerCase().localeCompare(b.value.toLowerCase());
                  })}
                  parse={(option: any) => option.value}
                  getOptionLabel={(option: any) => `${option.value} - ${option.label}`}
                  format={(partyType: string) => partyTypeOptions.find((option: any) => option.value === partyType)}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isSearchable={true}
                  isClearable={false}
                  menuPosition="fixed"
                />
                <FormikTextField label="First Name" name="firstName" type="string" />
                <FormikTextField label="Last Name" name="lastName" type="string" />
                <FormikSelectField
                  name="birthCountry"
                  label="Birth Country"
                  parse={(option: IPartialCountryCode) => option.alphaThreeCode}
                  format={(countryCode: string) =>
                    countryCodeOptions?.find(
                      (opt: IPartialCountryCode) => opt?.alphaThreeCode?.toUpperCase() === countryCode?.toUpperCase()
                    )
                  }
                  options={countryCodeOptions}
                  getOptionLabel={(option: IPartialCountryCode) => `${option.countryName} - ${option.alphaThreeCode}`}
                  getOptionValue={(option: IPartialCountryCode) => `${option.alphaThreeCode}`}
                  closeMenuOnSelect={false}
                  isClearable={false}
                  menuPosition="fixed"
                />
                <FormikTextField label="Birth City" name="birthCity" type="string" />
                <FormikSelectField
                  name="nationality"
                  label="Nationality"
                  parse={(option: IPartialCountryCode) => option.alphaThreeCode}
                  format={(countryCode: string) =>
                    countryCodeOptions?.find(
                      (opt: IPartialCountryCode) => opt?.alphaThreeCode?.toUpperCase() === countryCode?.toUpperCase()
                    )
                  }
                  options={countryCodeOptions}
                  getOptionLabel={(option: IPartialCountryCode) => `${option.countryName} - ${option.alphaThreeCode}`}
                  getOptionValue={(option: IPartialCountryCode) => `${option.alphaThreeCode}`}
                  closeMenuOnSelect={false}
                  isClearable={false}
                  menuPosition="fixed"
                />
                <FormikSelectField
                  name="APISEmploymentCategory"
                  label="Employment Category"
                  options={employmentCategoryOptions}
                  parse={(option: any) => option.value}
                  getOptionLabel={(option: any) => option.value}
                  format={(empCat: string) => employmentCategoryOptions.find((option: any) => option.value === empCat)}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isSearchable={true}
                  isClearable={false}
                  menuPosition="fixed"
                />
                <FormikSelectField
                  name="gender"
                  label="Gender"
                  parse={(option: any) => option.value}
                  format={(gender: string) => genderOptions.find((option: any) => option.value === gender)}
                  options={genderOptions}
                  getOptionLabel={(option: any) => `${option.value} - ${option.label}`}
                  closeMenuOnSelect={false}
                  isClearable={false}
                  menuPosition="fixed"
                />
                <FormikDateTimeField
                  label="DOB"
                  name="dateOfBirth"
                  hideTimeControl={true}
                  maxDate={new Date()?.valueOf()}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
                  Optional Fields
                </Typography>
                <FormikTextField label="Middle Name" name="middleName" type="string" />
                <FormikTextField
                  label="Street Address"
                  name="streetAddress"
                  type="string"
                  multiline={true}
                  minRows={1}
                  maxRows={2}
                />
                <FormikTextField label="City" name="city" type="string" />
                <FormikTextField label="Province" name="provOrState" type="string" />
                <FormikTextField label="Postal Code" name="postal" type="string" />
                <FormikSelectField
                  name="country"
                  label="Country"
                  parse={(option: IPartialCountryCode) => option?.alphaThreeCode ?? null}
                  format={(countryCode: string) =>
                    countryCodeOptions?.find(
                      (opt: IPartialCountryCode) => opt?.alphaThreeCode?.toUpperCase() === countryCode?.toUpperCase()
                    )
                  }
                  options={countryCodeOptions}
                  getOptionLabel={(option: IPartialCountryCode) => `${option?.countryName} - ${option?.alphaThreeCode}`}
                  getOptionValue={(option: IPartialCountryCode) => `${option?.alphaThreeCode}`}
                  closeMenuOnSelect={false}
                  isClearable={true}
                  menuPosition="fixed"
                />

                <FormikSelectField
                  name="countryOfResidence"
                  label="Country Of Residence"
                  parse={(option: IPartialCountryCode) => option?.alphaThreeCode ?? null}
                  format={(countryCode: string) =>
                    countryCodeOptions?.find(
                      (opt: IPartialCountryCode) => opt?.alphaThreeCode?.toUpperCase() === countryCode?.toUpperCase()
                    )
                  }
                  options={countryCodeOptions}
                  getOptionLabel={(option: IPartialCountryCode) => `${option?.countryName} - ${option?.alphaThreeCode}`}
                  getOptionValue={(option: IPartialCountryCode) => `${option?.alphaThreeCode}`}
                  closeMenuOnSelect={false}
                  isClearable={true}
                  menuPosition="fixed"
                />

                <FormikTextField label="Birth State" name="birthState" type="string" />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
                  Documents
                </Typography>
                {documents.map((doc: IDocument, key: number) => {
                  return (
                    <DraggableListItem
                      active={false}
                      key={key}
                      onClick={() => {
                        setSelectedDocument(doc);
                        setOpenDocumentModal(true);
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      {doc.documentType} - {getEnumValueFromKey(PartyDocumentNameCode, doc.documentType)}
                    </DraggableListItem>
                  );
                })}
                <Button
                  fullWidth
                  color="success"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setSelectedDocument(undefined);
                    setOpenDocumentModal(true);
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  <AddIcon fontSize="small" /> Add Document
                </Button>
              </Grid>
              <Grid item xs={12}>
                <ButtonGroup variant="contained">
                  <Button color="success" disabled={isSubmitting || !dirty || !isValid} type="submit">
                    {selectedParty ? "Update Party" : "Create New Party"}
                  </Button>
                  <Button
                    type="reset"
                    color="warning"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={() => {
                      handleReset();
                      setDocuments(selectedParty?.documents ?? []);
                    }}
                  >
                    Reset
                  </Button>
                  {selectedParty && (
                    <Button color="error" disabled={!selectedParty || isSubmitting || dirty} onClick={handleDelete}>
                      Delete Party
                    </Button>
                  )}
                </ButtonGroup>
              </Grid>
            </Grid>
            <Modal open={openDocumentModal}>
              <Paper elevation={4} sx={{ p: 4, width: "40%", minWidth: 350, margin: "auto auto" }}>
                <APISDocumentField
                  selectedDocument={selectedDocument}
                  updateDocument={handleUpdateDocuments}
                  setActiveModal={props.setActiveModal}
                  countryCodeOptions={countryCodeOptions}
                  setOpenDocumentModal={setOpenDocumentModal}
                  setSelectedDocument={setSelectedDocument}
                />
              </Paper>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
